/**
 * Conectores lingüísticos ("muletillas") que Eva dice en el MISMO tick en que
 * se envía el turno (gate 0 ms), mientras el low-api decide, para que el
 * candidato perciba una respuesta inmediata y una conversación fluida.
 *
 * Reglas del copy (español neutro, tuteo, sin voseo):
 *  - NUNCA evalúan la respuesta ("buena respuesta", "correcto").
 *  - NUNCA anuncian espera ("un momento", "dame un segundo"): suena a call
 *    center. El conector no promete nada, solo acusa recibo.
 *  - NUNCA interpretan intención (sin regex): el tono no depende de lo dicho.
 *  - Máximo UN acuse por turno; el saludo inicial NO lleva acuse local (el BE
 *    es dueño del nombre).
 *  - NUNCA prometen un desenlace ("seguimos", "avanzamos", "entendido"): el BE
 *    puede responder con una contrapregunta sobre la MISMA pregunta y el
 *    conector quedaría contradictorio.
 *  - Cortos (<= ~0.7 s hablados) y >= 4 caracteres (menos glitchea el TTS).
 */

/** Tipo de turno que originó el conector (lo decide el runtime). */
export type ConnectorKind =
  | 'content'
  | 'command_pass'
  | 'command_clarify'
  | 'command_repeat'
  | 'think';

export type ConnectorCategory =
  | 'long'
  | 'content'
  | 'command_pass'
  | 'command_clarify'
  | 'command_repeat'
  | 'think'
  | 'already_said';

/**
 * Los pools evitan tokens sueltos "ecoables" (el micrófono capta la cola del
 * propio conector: "detalle", "acuerdo", "okey"): por eso no hay "Okey." ni
 * "Gracias por el detalle.", y las respuestas cortas (<8 palabras) no llevan
 * conector. El FE NO interpreta intención (sin regex): solo longitud.
 */
export const FC_LOW_CONNECTORS: Record<ConnectorCategory, readonly string[]> = {
  content: ['Ya veo.', 'Gracias por contarme.', 'Comprendo.', 'Vale, gracias.', 'Gracias por compartirlo.', 'Ajá, ya veo.'],
  long: [
    'Gracias por contarlo con detalle.',
    'Gracias por el contexto.',
    'Gracias por explicarlo.',
    'Ya veo, gracias por contarme.',
    'Comprendo, gracias.',
  ],
  command_pass: ['De acuerdo.', 'Está bien.', 'Listo.', 'Sin problema.'],
  command_clarify: ['Claro.', 'Con gusto.'],
  // La repetición es local (repeatLastTurn): sin conector.
  command_repeat: [],
  // "think" (pedir tiempo para pensar): sin conector, no hay nada que acusar.
  think: [],
  already_said: ['Cierto, ya lo mencionaste.', 'Sí, eso ya me lo dijiste.'],
};

/** Cuántos conectores recientes no se pueden repetir. */
export const FC_LOW_CONNECTOR_NO_REPEAT = 3;

const LONG_WORDS = 40;
const CONTENT_WORDS = 8;

/** Palabras aproximadas de la respuesta (para clasificar longitud). */
function wordCount(text: string): number {
  const t = (text || '').trim();
  return t ? t.split(/\s+/).length : 0;
}

/**
 * Categoría de conector para un turno de contenido, SOLO por longitud
 * (sin regex de intención). Menos de 8 palabras => null (sin conector).
 */
export function categorizeAnswer(transcript: string): ConnectorCategory | null {
  const words = wordCount(transcript);
  if (words >= LONG_WORDS) return 'long';
  if (words >= CONTENT_WORDS) return 'content';
  return null;
}

export interface PickConnectorOptions {
  /** Últimos conectores dichos (más reciente al final); no se repiten. */
  recent?: readonly string[];
  /** Inyectable para tests. */
  rand?: () => number;
}

function categoryFor(transcript: string, kind: ConnectorKind): ConnectorCategory | null {
  switch (kind) {
    case 'content':
      return categorizeAnswer(transcript);
    default:
      return kind;
  }
}

/**
 * Elige un conector por categoría, sin repetir ninguno de los últimos
 * `FC_LOW_CONNECTOR_NO_REPEAT`. Si el pool se agota (pools de 2 elementos),
 * degrada a "no repetir el último" y, por último, al pool completo.
 * Devuelve null cuando no hay conector (repeat/think, o contenido < 8 palabras).
 */
export function pickConnector(
  transcript: string,
  kind: ConnectorKind = 'content',
  opts: PickConnectorOptions = {},
): string | null {
  const category = categoryFor(transcript, kind);
  if (!category) return null;
  return pickFromPool(FC_LOW_CONNECTORS[category], opts);
}

export function pickConnectorByCategory(
  category: string,
  opts: PickConnectorOptions = {},
): string | null {
  if (!Object.prototype.hasOwnProperty.call(FC_LOW_CONNECTORS, category)) return null;
  return pickFromPool(FC_LOW_CONNECTORS[category as ConnectorCategory], opts);
}

function pickFromPool(pool: readonly string[], opts: PickConnectorOptions): string | null {
  if (pool.length === 0) return null;
  const rand = opts.rand ?? Math.random;
  const recent = (opts.recent ?? []).slice(-FC_LOW_CONNECTOR_NO_REPEAT);
  let list: readonly string[] = pool.filter((p) => !recent.includes(p));
  if (list.length === 0) {
    const last = recent[recent.length - 1];
    list = pool.filter((p) => p !== last);
  }
  if (list.length === 0) list = pool;
  const idx = Math.min(list.length - 1, Math.floor(rand() * list.length));
  return list[idx];
}
